import React, { useState } from 'react';
import { useNavigation } from '../context/NavigationContext';
import { SectionHeading } from '../components/ui/SectionHeading';
import {
  Mail,
  Phone,
  MapPin,
  Clock,
  Send,
  CheckCircle2,
  MessageSquare,
  ShieldCheck,
  Building2,
} from 'lucide-react';

export const ContactPage: React.FC = () => {
  const { navigate } = useNavigation();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    sector: 'CORPORATE',
    subject: '',
    message: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!formData.fullName.trim() || !formData.email.trim() || !formData.message.trim()) {
      setError('Lütfen zorunlu alanları doldurunuz.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/marketing/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject || `${formData.sector} Sektörel İletişim`,
          message: formData.message,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Mesaj gönderilemedi.');
      }
      setSubmitted(true);
    } catch (err: any) {
      setError(err?.message || 'Mesaj iletilemedi. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 pt-28 sm:pt-36 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          badge="İletişim & Destek"
          title="Sorularınız İçin Buradayız"
          subtitle="Sektörel çözümlerimiz, kurumsal paketler veya teknik entegrasyonlar hakkında uzman ekibimizle görüşün."
        />

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Left Column: Contact Cards & Address */}
          <div className="lg:col-span-5 space-y-6">
            <div className="p-8 rounded-3xl bg-slate-900/80 border border-slate-800 space-y-6">
              <h3 className="text-xl font-bold text-white">İletişim Kanalları</h3>
              <p className="text-xs text-slate-400 leading-relaxed">
                Taleplerinize mesai saatleri içinde ortalama 15 dakika içerisinde geri dönüş sağlanmaktadır.
              </p>

              <div className="space-y-4 text-xs sm:text-sm">
                <div className="flex items-start gap-3.5 p-3 rounded-xl bg-slate-950/60 border border-slate-800/80">
                  <div className="w-9 h-9 rounded-lg bg-blue-600/10 text-blue-400 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4" />
                  </div>
                  <div>
                    <strong className="text-white block font-semibold">Resmi Şirket Adresi</strong>
                    <span className="text-slate-300 font-medium">İstanbul / Muğla</span>
                  </div>
                </div>

                <div className="flex items-start gap-3.5 p-3 rounded-xl bg-slate-950/60 border border-slate-800/80">
                  <div className="w-9 h-9 rounded-lg bg-emerald-600/10 text-emerald-400 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-4 h-4" />
                  </div>
                  <div>
                    <strong className="text-white block font-semibold">Telefon & WhatsApp</strong>
                    <a
                      href="tel:+905323712548"
                      className="text-slate-300 hover:text-emerald-400 font-medium transition block"
                    >
                      0532 371 2548
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3.5 p-3 rounded-xl bg-slate-950/60 border border-slate-800/80">
                  <div className="w-9 h-9 rounded-lg bg-indigo-600/10 text-indigo-400 border border-indigo-500/20 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-4 h-4" />
                  </div>
                  <div>
                    <strong className="text-white block font-semibold">E-Posta</strong>
                    <a
                      href="mailto:info@adiosoft.com"
                      className="text-slate-300 hover:text-indigo-400 font-medium transition block"
                    >
                      info@adiosoft.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3.5 p-3 rounded-xl bg-slate-950/60 border border-slate-800/80">
                  <div className="w-9 h-9 rounded-lg bg-amber-600/10 text-amber-400 border border-amber-500/20 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-4 h-4" />
                  </div>
                  <div>
                    <strong className="text-white block font-semibold">Çalışma Saatleri</strong>
                    <span className="text-slate-400">Pazartesi - Cuma: 09:00 - 18:30 (7/24 Acil Sunucu Takibi)</span>
                  </div>
                </div>
              </div>
            </div>

            {/* SLA Badge card */}
            <div className="p-6 rounded-2xl bg-gradient-to-r from-blue-900/20 to-slate-900 border border-slate-800 flex items-center gap-4">
              <ShieldCheck className="w-8 h-8 text-blue-400 flex-shrink-0" />
              <div>
                <h4 className="text-white font-bold text-xs">Kurumsal Destek Garantisi</h4>
                <p className="text-slate-400 text-[11px] mt-0.5">
                  Tüm teknik sorularınız kıdemli SaaS mühendislerimiz tarafından yanıtlanır.
                </p>
              </div>
            </div>
          </div>

          {/* Right Column: Contact Form */}
          <div className="lg:col-span-7">
            <div className="p-8 sm:p-10 rounded-3xl bg-slate-900/90 border border-slate-800 shadow-2xl">
              {submitted ? (
                <div className="text-center py-12 space-y-4">
                  <div className="w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center mx-auto">
                    <CheckCircle2 className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Mesajınız Alındı!</h3>
                  <p className="text-slate-400 text-sm max-w-md mx-auto">
                    Talebiniz müşteri temsilcimize iletildi. En kısa sürede belirttiğiniz iletişim bilgileri üzerinden sizinle irtibata geçeceğiz.
                  </p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({
                        fullName: '',
                        email: '',
                        phone: '',
                        sector: 'CORPORATE',
                        subject: '',
                        message: '',
                      });
                    }}
                    className="px-6 py-2.5 rounded-xl bg-slate-800 text-white text-xs font-semibold hover:bg-slate-700 transition"
                  >
                    Yeni Mesaj Gönder
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {error && (
                    <div className="p-3.5 bg-rose-500/10 border border-rose-500/20 rounded-xl text-rose-300 text-xs">
                      {error}
                    </div>
                  )}
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="w-4 h-4 text-blue-400" />
                    <h3 className="text-lg font-bold text-white">Bize Mesaj Gönderin</h3>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1.5">
                        Adınız Soyadınız *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.fullName}
                        onChange={(e) =>
                          setFormData({ ...formData, fullName: e.target.value })
                        }
                        placeholder="Örn. Ahmet Yılmaz"
                        className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1.5">
                        E-Posta Adresiniz *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        placeholder="ahmet@sirketiniz.com"
                        className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1.5">
                        Telefon Numaranız
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) =>
                          setFormData({ ...formData, phone: e.target.value })
                        }
                        placeholder="+90 5XX XXX XX XX"
                        className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-slate-300 mb-1.5">
                        İlgilendiğiniz Sektör
                      </label>
                      <select
                        value={formData.sector}
                        onChange={(e) =>
                          setFormData({ ...formData, sector: e.target.value })
                        }
                        className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-white focus:outline-none focus:border-blue-500 transition"
                      >
                        <option value="CORPORATE">Kurumsal & Holding</option>
                        <option value="YACHT_RENTAL">Lüks Yat Kiralama & Mavi Tur</option>
                        <option value="LEGAL">Hukuk & Avukatlık</option>
                        <option value="HEALTH">Sağlık & Klinik</option>
                        <option value="OTHER">Diğer Sektörler</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1.5">
                      Konu Başlığı
                    </label>
                    <input
                      type="text"
                      value={formData.subject}
                      onChange={(e) =>
                        setFormData({ ...formData, subject: e.target.value })
                      }
                      placeholder="Örn. Yat kiralama şablonu ve özel domain kurulumu hk."
                      className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1.5">
                      Mesajınız *
                    </label>
                    <textarea
                      required
                      rows={4}
                      value={formData.message}
                      onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value })
                      }
                      placeholder="Web sitenizle ilgili gereksinimlerinizi veya sorularınızı buraya yazabilirsiniz..."
                      className="w-full px-4 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-xs sm:text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3.5 px-6 rounded-xl bg-blue-600 hover:bg-blue-500 text-white font-bold text-xs sm:text-sm shadow-lg shadow-blue-600/30 flex items-center justify-center gap-2 transition active:scale-95 disabled:opacity-50"
                  >
                    {loading ? (
                      <span>İletiliyor...</span>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        <span>Mesajı İletin</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
